/**
 * Error handling utilities for Firebase and application-specific errors
 */
import { FirebaseError } from 'firebase/app';

// Define error types
export const ErrorTypes = {
  // Firebase error types
  FIREBASE_AUTH: 'firebase/auth',
  FIREBASE_FIRESTORE: 'firebase/firestore',
  FIREBASE_STORAGE: 'firebase/storage',
  FIREBASE_FUNCTIONS: 'firebase/functions',
  
  // Application error types
  VALIDATION: 'app/validation',
  PERMISSION: 'app/permission',
  RATE_LIMIT: 'app/rate-limit',
  TOURNAMENT: 'app/tournament',
  NETWORK: 'app/network',
  UNKNOWN: 'app/unknown'
};

// Firebase error codes that should be mapped to permission errors
const PERMISSION_ERROR_CODES = [
  'permission-denied',
  'PERMISSION_DENIED',
  'storage/unauthorized',
  'auth/insufficient-permission'
];

// Simple rate limiter implementation
class RateLimiter {
  constructor(maxRequests = 5, timeWindowMs = 60000) {
    this.maxRequests = maxRequests;
    this.timeWindowMs = timeWindowMs;
    this.requestTimestamps = [];
  }

  async execute(callback) {
    // Clean up old timestamps
    const now = Date.now();
    this.requestTimestamps = this.requestTimestamps.filter(
      timestamp => now - timestamp < this.timeWindowMs
    );

    // Check if rate limit exceeded
    if (this.requestTimestamps.length >= this.maxRequests) {
      throw new ApplicationError(
        'Too many requests. Please try again later.',
        ErrorTypes.RATE_LIMIT,
        429
      );
    }

    // Add current timestamp and execute callback
    this.requestTimestamps.push(now);
    return callback();
  }
}

// Create rate limiters for different operations
export const tournamentRateLimiter = new RateLimiter(5, 60000); // 5 requests per minute
export const authRateLimiter = new RateLimiter(10, 60000); // 10 requests per minute
export const storageRateLimiter = new RateLimiter(20, 60000); // 20 requests per minute

/**
 * Custom error class for application errors
 */
class ApplicationError extends Error {
  constructor(message, type = ErrorTypes.UNKNOWN, code = 500, originalError = null) {
    super(message);
    this.name = 'ApplicationError';
    this.type = type;
    this.code = code;
    this.originalError = originalError;
  }
}

/**
 * Convert Firebase error to application error
 * 
 * @param {FirebaseError} error - Firebase error
 * @return {ApplicationError} Converted application error
 */
function convertFirebaseError(error) {
  let type = ErrorTypes.UNKNOWN;
  let code = 500;
  let message = error.message;

  // Determine error type based on Firebase error code
  if (error.code?.startsWith('auth/')) {
    type = ErrorTypes.FIREBASE_AUTH;
    code = 401;
  } else if (error.code?.startsWith('firestore/')) {
    type = ErrorTypes.FIREBASE_FIRESTORE;
    code = 400;
  } else if (error.code?.startsWith('storage/')) {
    type = ErrorTypes.FIREBASE_STORAGE;
    code = 400;
  } else if (error.code?.startsWith('functions/')) {
    type = ErrorTypes.FIREBASE_FUNCTIONS;
    code = 400;
  }

  // Check for permission errors
  if (PERMISSION_ERROR_CODES.includes(error.code)) {
    type = ErrorTypes.PERMISSION;
    code = 403;
    message = 'You do not have permission to perform this action.';
  }

  // Network errors
  if (error.code === 'network-request-failed') {
    type = ErrorTypes.NETWORK;
    code = 503;
    message = 'Network error. Please check your connection and try again.';
  }

  return new ApplicationError(message, type, code, error);
}

/**
 * Handle Firebase errors and return appropriate response
 * 
 * @param {Error} error - Error object
 * @param {string} operation - Name of the operation that failed
 * @param {boolean} throwError - Whether to throw the error or return it
 * @return {Object} Error response object
 */
export const handleFirebaseError = (error, operation = 'unknown', throwError = false) => {
  let appError;

  // Convert to ApplicationError if it's a Firebase error
  if (error instanceof FirebaseError) {
    appError = convertFirebaseError(error);
  } else if (error instanceof ApplicationError) {
    appError = error;
  } else {
    appError = new ApplicationError(
      error.message || 'An unknown error occurred',
      ErrorTypes.UNKNOWN,
      500,
      error
    );
  }

  // Log the error for debugging
  console.error(`Error in operation ${operation}:`, appError);

  // Format error response object
  const errorResponse = {
    success: false,
    error: {
      message: appError.message,
      type: appError.type,
      code: appError.code,
      operation
    }
  };

  // Either throw or return the error
  if (throwError) {
    throw appError;
  }

  return errorResponse;
};

/**
 * Validate a value against specified rules
 * 
 * @param {*} value - The value to validate
 * @param {Object} rules - Validation rules
 * @return {Object} Validation result
 */
export const validateInput = (value, rules) => {
  const errors = [];

  if (rules.required && (value === undefined || value === null || value === '')) {
    errors.push('Value is required');
  }

  if (rules.minLength && typeof value === 'string' && value.length < rules.minLength) {
    errors.push(`Value must be at least ${rules.minLength} characters`);
  }

  if (rules.maxLength && typeof value === 'string' && value.length > rules.maxLength) {
    errors.push(`Value must be no more than ${rules.maxLength} characters`);
  }

  if (rules.pattern && typeof value === 'string' && !rules.pattern.test(value)) {
    errors.push(rules.patternMessage || 'Value does not match required pattern');
  }

  if (rules.min && typeof value === 'number' && value < rules.min) {
    errors.push(`Value must be at least ${rules.min}`);
  }

  if (rules.max && typeof value === 'number' && value > rules.max) {
    errors.push(`Value must be no more than ${rules.max}`);
  }

  if (rules.custom && typeof rules.custom === 'function') {
    const customResult = rules.custom(value);
    if (customResult !== true) {
      errors.push(customResult || 'Value failed custom validation');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Create a validation error
 * 
 * @param {string} message - Error message
 * @param {Array} validationErrors - List of validation errors
 * @return {ApplicationError} Validation error
 */
export const createValidationError = (message, validationErrors = []) => {
  const error = new ApplicationError(
    message || 'Validation error',
    ErrorTypes.VALIDATION,
    400
  );
  error.validationErrors = validationErrors;
  return error;
};

/**
 * Create a permission error
 * 
 * @param {string} message - Error message
 * @return {ApplicationError} Permission error
 */
export const createPermissionError = (message) => {
  return new ApplicationError(
    message || 'You do not have permission to perform this action',
    ErrorTypes.PERMISSION,
    403
  );
};

/**
 * Handle errors globally and display appropriate UI feedback
 * 
 * @param {Error} error - Error object
 * @param {Function} displayErrorCallback - Function to display error UI
 */
export const handleUIError = (error, displayErrorCallback) => {
  let errorMessage = 'An unexpected error occurred. Please try again.';
  let errorType = ErrorTypes.UNKNOWN;
  
  if (error instanceof ApplicationError) {
    errorMessage = error.message;
    errorType = error.type;
  } else if (error instanceof FirebaseError) {
    const appError = convertFirebaseError(error);
    errorMessage = appError.message;
    errorType = appError.type;
  }

  // Log for debugging
  console.error('UI Error:', error);
  
  // Use the callback to display error in UI
  if (typeof displayErrorCallback === 'function') {
    displayErrorCallback(errorMessage, errorType);
  }
}; 